import type { AlertCategories } from "@weather-deck/shared";
import { el } from "./dom";

const LS_ID = "weather-deck.subId";
const LS_CATS = "weather-deck.cats";
const DEFAULT_CATS: AlertCategories = { storm: true, frost: true, heat: true, highPm25: true, sensorOffline: true };

function loadCats(): AlertCategories {
  const raw = localStorage.getItem(LS_CATS);
  return raw ? (JSON.parse(raw) as AlertCategories) : { ...DEFAULT_CATS };
}
function saveCats(c: AlertCategories): void { localStorage.setItem(LS_CATS, JSON.stringify(c)); }

function urlB64ToUint8Array(b64: string): BufferSource {
  const padding = "=".repeat((4 - b64.length % 4) % 4);
  const normalized = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(normalized);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out as unknown as BufferSource;
}

export type SubscribeOutcome =
  | { ok: true }
  | { ok: false; reason: "unsupported" | "permission-denied" | "error" | "timeout"; detail?: string };

class TimeoutError extends Error {}

/**
 * navigator.serviceWorker.ready has exactly one known way to hang forever:
 * a page whose first load isn't yet controlled by any worker (see sw.ts's
 * activate handler for why). There may be others. A timeout turns any of
 * them into a reported error instead of "Enabling…" sitting there forever
 * with no way to tell a slow network from a permanent hang.
 */
function withTimeout<T>(p: Promise<T>, ms: number, message: string): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) => setTimeout(() => reject(new TimeoutError(message)), ms))
  ]);
}

/**
 * Every early return here used to be silent — tap the button, nothing
 * visibly happens whether it worked or not. That's indistinguishable from
 * "it worked, the alert just hasn't fired yet," which is exactly the state
 * that made a real dropout unverifiable. Now every path reports why.
 */
async function ensureSubscription(apiBase: string): Promise<SubscribeOutcome> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return { ok: false, reason: "unsupported" };
  }

  let permission: NotificationPermission;
  try {
    permission = await Notification.requestPermission();
  } catch (e) {
    return { ok: false, reason: "unsupported", detail: e instanceof Error ? e.message : String(e) };
  }
  if (permission !== "granted") {
    return { ok: false, reason: "permission-denied" };
  }

  try {
    const reg = await withTimeout(navigator.serviceWorker.ready, 8000, "service worker never took control of this page");
    const existing = await reg.pushManager.getSubscription();
    const vapid = (await (await fetch("/vapid.json")).json()) as { publicKey: string };

    const sub = existing ?? await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(vapid.publicKey)
    });

    const p256dh = sub.getKey("p256dh");
    const auth = sub.getKey("auth");
    if (!p256dh || !auth) return { ok: false, reason: "error", detail: "browser did not return subscription keys" };

    const payload = {
      id: localStorage.getItem(LS_ID) ?? undefined,
      endpoint: sub.endpoint,
      keys: {
        p256dh: btoa(String.fromCharCode(...new Uint8Array(p256dh))),
        auth:   btoa(String.fromCharCode(...new Uint8Array(auth)))
      },
      subscribedAt: new Date().toISOString(),
      categories: loadCats()
    };

    const resp = await fetch(`${apiBase}/api/subscribe`, {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify(payload)
    });
    if (!resp.ok) return { ok: false, reason: "error", detail: `server returned ${resp.status}` };

    const { id } = (await resp.json()) as { id: string };
    localStorage.setItem(LS_ID, id);
    return { ok: true };
  } catch (e) {
    if (e instanceof TimeoutError) return { ok: false, reason: "timeout", detail: e.message };
    return { ok: false, reason: "error", detail: e instanceof Error ? e.message : String(e) };
  }
}

async function sendTestAlert(apiBase: string, status: HTMLElement): Promise<void> {
  status.textContent = "Sending…";
  try {
    const resp = await fetch(`${apiBase}/api/test-sensor-alert`, { method: "POST" });
    const body = (await resp.json()) as {
      ok: boolean; sent?: number; total?: number; error?: string;
      failures?: Array<{ id: string; status?: number; error?: string }>;
    };
    if (!body.ok) {
      status.textContent = body.error ?? "Test alert failed.";
      return;
    }
    const sent = body.sent ?? 0;
    const total = body.total ?? 0;
    if (sent > 0) {
      status.textContent = `Test sent to ${sent} device${sent === 1 ? "" : "s"}.`;
    } else if (total === 0) {
      // Genuinely nobody subscribed — distinct from the next case below.
      status.textContent = "No subscribed devices — tap Enable push above first.";
    } else {
      // A subscription exists but every send to it failed — surfacing this
      // instead of the line above, since that would read as "never subscribed"
      // when the real problem is a stale or broken subscription. Prefer the
      // raw error text over a bare status code: plenty of push failures
      // (network errors, bad VAPID setup) never carry an HTTP status at all,
      // and "(code ?)" said nothing useful when that happened.
      const details = (body.failures ?? [])
        .map((f) => f.error ?? (f.status != null ? `HTTP ${f.status}` : "unknown error"))
        .join("; ");
      status.textContent = `Subscribed, but delivery failed for ${total} device(s)` +
        (details ? `: ${details}` : "") + " — try Enable push again.";
    }
  } catch {
    status.textContent = "Test alert failed — network error.";
  }
}

function toggle(key: keyof AlertCategories, label: string, cats: AlertCategories): HTMLLabelElement {
  const input = el("input", { type: "checkbox" }) as HTMLInputElement;
  input.checked = cats[key];
  input.dataset.cat = key;
  input.addEventListener("change", () => {
    cats[key] = input.checked;
    saveCats(cats);
  });
  const wrapper = document.createElement("label");
  wrapper.append(input, document.createTextNode(` ${label}`));
  return wrapper;
}

const UNSUPPORTED_MSG = "Push isn't available here — open this app from the icon on your Home Screen " +
  "(Safari, iOS 16.4+ required), not a browser tab.";
const DENIED_MSG = "Notification permission was denied. Enable it in iOS Settings → Weather Deck → Notifications, then try again.";
const TIMEOUT_MSG = "Timed out waiting for the app to finish loading. Reload the page once, then try Enable push again.";

export function buildAlertsPanel(apiBase: string): HTMLElement {
  const cats = loadCats();

  const enableStatus = el("p", { className: "alerts-enable-status" });
  const button = el("button", { text: "Enable push" }) as HTMLButtonElement;
  button.addEventListener("click", () => {
    void (async () => {
      enableStatus.textContent = "Enabling…";
      const r = await ensureSubscription(apiBase);
      if (r.ok) {
        enableStatus.textContent = "Push enabled on this device.";
      } else if (r.reason === "unsupported") {
        enableStatus.textContent = UNSUPPORTED_MSG;
      } else if (r.reason === "permission-denied") {
        enableStatus.textContent = DENIED_MSG;
      } else if (r.reason === "timeout") {
        enableStatus.textContent = TIMEOUT_MSG;
      } else {
        enableStatus.textContent = `Could not enable push${r.detail ? ` (${r.detail})` : ""}.`;
      }
    })();
  });

  const testStatus = el("p", { className: "alerts-test-status" });
  const testButton = el("button", { className: "alerts-test-button", text: "Send test alert" }) as HTMLButtonElement;
  testButton.addEventListener("click", () => { void sendTestAlert(apiBase, testStatus); });

  return el("section", { className: "alerts-panel" }, [
    el("h3", { text: "Alerts" }),
    toggle("storm",    "Storm / heavy rain / wind", cats),
    toggle("frost",    "Frost / ice", cats),
    toggle("heat",     "Heat", cats),
    toggle("highPm25", "High PM2.5 (>25 µg/m³)", cats),
    toggle("sensorOffline", "senseBox mfreiheit offline", cats),
    button,
    enableStatus,
    testButton,
    testStatus
  ]);
}
